import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";
import { MobileMenuContent } from "@/components/MobileMenuContent";
import { useState, useMemo } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import logoWhite from "@/assets/logo-white.svg";
import logoBlack from "@/assets/logo-black.svg";
import Footer from "@/components/Footer";
import { CtaFooter, DynamicCtaFooter, PageSeo } from "@/components/ds";
import LearnContentSection from "@/components/learn/LearnContentSection";
import LearnTopicNav from "@/components/learn/LearnTopicNav";
import { useWaitlistModal } from "@/components/waitlist/WaitlistContext";
import { useTopics } from "@/hooks/useTopics";
import { useArticlesGroupedByTopic } from "@/hooks/useArticlesByTopic";
import learnCtaBg from "@/assets/learn-cta-bg.jpg";
import { usePrerenderReady } from "@/hooks/usePrerenderReady";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { SITE_URL, buildUrl } from "@/config/site";
import { usePublishedPosts } from "@/hooks/usePosts";

const NavButtons = ({ visible, onWaitlistClick }: { visible?: boolean; onWaitlistClick: (triggerElement?: string) => void }) => (
  <div className="flex items-center gap-8">
    <NavbarButton href="/contact" variant="secondary" visible={visible}>Contact</NavbarButton>
    <NavbarButton variant="primary" visible={visible} onClick={() => onWaitlistClick('navbar_cta')}>Join Waitlist</NavbarButton>
  </div>
);

export default function Learn() {
  useScrollDepth();
  const { data: allPublishedPosts } = usePublishedPosts();

  const navItems = [
    { name: "Invest", link: "/invest" },
    { name: "Learn", link: "/learn" },
    { name: "About", link: "/about" },
  ];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState("All");
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [lastSectionBg, setLastSectionBg] = useState<'white' | 'platinum'>('white');
  const { openModal } = useWaitlistModal();
  const { data: topics, isLoading: topicsLoading } = useTopics();
  const { data: articlesGrouped, isLoading: articlesLoading } = useArticlesGroupedByTopic();

  // Signal to Prerender.io when content is loaded
  const isContentLoaded = !topicsLoading && !articlesLoading;
  usePrerenderReady(isContentLoaded);

  // Build secondary nav items for topics with articles
  const topicSecondaryNavItems = useMemo(() => {
    const topicsWithArticles = (topics?.filter(t => t.is_active) || []).filter(topic => {
      const articles = articlesGrouped?.[topic.id];
      return articles && articles.length > 0;
    });
    
    return [
      { label: "All", anchor: "all", onClick: () => handleTopicChange("All") },
      ...topicsWithArticles.map(t => ({
        label: t.name,
        anchor: t.slug,
        onClick: () => handleTopicChange(t.name),
      })),
    ];
  }, [topics, articlesGrouped]);

  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const scrollDelta = latest - lastScrollY;
    
    // Always show when at the top of the page
    if (latest <= 50) {
      setShowSecondaryNav(true);
    }
    // Only trigger hide after scrolling down 50px+
    else if (scrollDelta > 10 && latest > 100) {
      setShowSecondaryNav(false);
    }
    // Only trigger show after scrolling up 30px+
    else if (scrollDelta < -30) {
      setShowSecondaryNav(true);
    }
    
    // Only update lastScrollY if we've moved significantly (reduces jitter)
    if (Math.abs(scrollDelta) > 5) {
      setLastScrollY(latest);
    }
  });

  const handleTopicChange = (topic: string) => {
    setActiveTopic(topic);
    // Scroll to top when changing topics
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative w-full min-h-screen bg-background">
      <PageSeo pageSlug="/learn">
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "CollectionPage",
                "@id": buildUrl('/learn'),
                "url": buildUrl('/learn'),
                "name": "Learn About Private Market Investing | Fedgroup Private Markets",
                "description": "Educational resources on private market investments. Learn about alternative assets, portfolio strategies, and how to invest in property, agriculture, renewables and more.",
                "isPartOf": { "@type": "WebSite", "@id": `${SITE_URL}/#website` },
                ...(allPublishedPosts && allPublishedPosts.length > 0 ? {
                  "mainEntity": {
                    "@type": "ItemList",
                    "itemListElement": allPublishedPosts.map((post, index) => ({
                      "@type": "ListItem",
                      "position": index + 1,
                      "url": buildUrl(`/blog/${post.slug}`),
                      "name": post.title,
                    }))
                  }
                } : {})
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": buildUrl('/') },
                  { "@type": "ListItem", "position": 2, "name": "Learn", "item": buildUrl('/learn') }
                ]
              }
            ]
          })}
        </script>
      </PageSeo>
      {/* Fixed navbar */}
      <Navbar forceVisible mobileMenuOpen={isMobileMenuOpen}>
        <NavBody>
          <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
          <NavItems items={navItems} />
          <NavButtons onWaitlistClick={openModal} />
        </NavBody>

        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            <MobileMenuContent
              navItems={navItems}
              onItemClick={() => setIsMobileMenuOpen(false)}
              onWaitlistClick={openModal}
              secondaryNavItems={topicSecondaryNavItems}
            />
          </MobileNavMenu>
        </MobileNav>
      </Navbar>

      {/* Secondary Topic Navigation - framer-motion animated */}
      <motion.div
        initial={{ y: 0 }}
        animate={{ y: showSecondaryNav ? 0 : -100 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-[44px] left-0 right-0 z-30 hidden lg:block"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "saturate(180%) blur(20px)",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex justify-center">
          <LearnTopicNav 
            activeTopic={activeTopic} 
            onTopicChange={handleTopicChange}
            visible={true}
          />
        </div>
      </motion.div>

      {/* Spacer for fixed navbar + secondary nav */}
      <div className="h-[44px] lg:h-[92px]" />

      {/* SEO H1 - visually hidden but accessible */}
      <h1 className="sr-only">Learn About Private Market Investing</h1>

      {/* Article Content */}
      <LearnContentSection
        activeTopic={activeTopic}
        onTopicChange={handleTopicChange}
        onLastSectionBgChange={setLastSectionBg}
      />

      {/* CTA Section - gradient auto-detects previous section background */}
      <DynamicCtaFooter 
        pageSlug="/learn"
        fallback={
          <CtaFooter 
            backgroundImage={learnCtaBg}
          />
        }
      />

      <Footer />
    </div>
  );
}
